import { useNuxtApp } from '#app'
import { ofetch } from 'ofetch'
import { reportAuthIssue } from '@/utils/authDiagnostics'

let lastSignOutRedirectAt = 0

function redirectToSignOut(nuxtApp, reason, context = {}) {
  const now = Date.now()
  const currentPath = nuxtApp?.$router?.currentRoute?.value?.path
  if (currentPath === '/sign-out' || currentPath === '/sign-in') return
  if (now - lastSignOutRedirectAt < 2000) return

  reportAuthIssue('redirect_to_sign_out', {
    reason,
    currentPath,
    ...context,
  })

  lastSignOutRedirectAt = now
  nuxtApp.$router.replace('/sign-out').catch((error) => {
    reportAuthIssue(
      'redirect_to_sign_out_router_push_failed',
      {
        reason,
        currentPath,
        ...context,
      },
      error,
    )
  })
}

async function apiFetch(path, params = {}, auth = true) {
  const nuxtApp = useNuxtApp()
  const app = nuxtApp.vueApp

  const session = app.config.globalProperties.$clerk?.session

  if (auth && !session) {
    console.log('No auth token. Logging out.')
    redirectToSignOut(nuxtApp, 'api_missing_clerk_session', {
      path,
      method: params.method || 'GET',
      auth,
    })
    return
  }

  let jwt = ''

  if (auth) {
    let token = ''
    try {
      token = await session.getToken({
        template: 'User_Template',
      })
    }
    catch (error) {
      reportAuthIssue(
        'api_get_token_failed',
        {
          path,
          method: params.method || 'GET',
        },
        error,
      )
      throw error
    }

    if (!token) {
      console.log('No clerk token. Logging out.')
      redirectToSignOut(nuxtApp, 'api_missing_clerk_token', {
        path,
        method: params.method || 'GET',
        auth,
      })
      return
    }

    jwt = `Bearer ${token}`
  }

  params = {
    ...params,
    headers: {
      ...params.headers,
      Authorization: jwt || '',
      Accept: 'application/json',
    },
    async onRequestError({ error, request }) {
      reportAuthIssue(
        'api_request_error',
        {
          path,
          requestUrl: request?.url || null,
          method: params.method || 'GET',
        },
        error,
      )
      app.config.globalProperties.$toastError(error?.toString())
    },
    async onResponseError({ request, response }) {
      if (request.url?.includes('affiliate')) {
        return
      }
      if (
        response.status === 401
        && response._data?.message?.includes('Invalid token')
      ) {
        console.log('Token expired. Logging out.')
        redirectToSignOut(nuxtApp, 'api_invalid_token_response', {
          path,
          requestUrl: request?.url || null,
          method: params.method || 'GET',
          status: response.status,
          responseMessage: response._data?.message,
        })
        return
      }

      if (response?.status === 401 || response?.status === 403) {
        reportAuthIssue('api_auth_error_response_without_logout', {
          path,
          requestUrl: request?.url || null,
          method: params.method || 'GET',
          status: response.status,
          responseMessage: response._data?.message || null,
        })
      }

      const multipleErrors = response._data.message?.errors || null

      if (multipleErrors) {
        const errorsArray = Object.keys(multipleErrors).map((e) => {
          return multipleErrors[e][0]
        })
        app.config.globalProperties.$toastError(errorsArray[0])
        return
      }

      if (typeof response._data === 'string') {
        app.config.globalProperties.$toastError(response._data)
        return
      }

      app.config.globalProperties.$toastError(
        response._data?.message?.toString(),
      )
    },
  }

  try {
    return await ofetch(path, params)
  }
  catch (error) {
    reportAuthIssue(
      'api_fetch_threw',
      {
        path,
        method: params.method || 'GET',
        auth,
      },
      error,
    )
    throw error
  }
}

// Rise API REST Method Helpers
export const api = {
  delete: async (path, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, {
      ...params,
      method: 'DELETE',
    })
  },
  get: async (path, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, params)
  },
  patch: async (path, data, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, {
      ...params,
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },
  patchFormData: async (path, data, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, {
      ...params,
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },
  post: async (path, data, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, {
      ...params,
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
  postFormData: async (path, data, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, {
      ...params,
      method: 'POST',
      body: data,
    })
  },
  put: async (path, data, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, {
      ...params,
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },
  putFormData: async (path, data, params = {}) => {
    const runtimeConfig = useRuntimeConfig()
    return await apiFetch(`${runtimeConfig.public.API_URL}${path}`, {
      ...params,
      method: 'PUT',
      body: data,
    })
  },
}

// External HTTP Calls
export const http = {
  delete: async (path, params = {}) => {
    return await apiFetch(path, { ...params, method: 'DELETE' }, false)
  },
  get: async (path, params = {}) => {
    return await apiFetch(
      path,
      {
        ...params,
        method: 'GET',
      },
      false,
    )
  },
  patch: async (path, data, params = {}) => {
    return await apiFetch(
      path,
      {
        ...params,
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      false,
    )
  },
  post: async (path, data, params = {}) => {
    return await apiFetch(
      path,
      {
        ...params,
        method: 'POST',
        body: JSON.stringify(data),
      },
      false,
    )
  },
  postFormData: async (path, data, params = {}) => {
    return await apiFetch(
      path,
      {
        ...params,
        method: 'POST',
        body: data,
      },
      false,
    )
  },
  put: async (path, data, params = {}) => {
    return await apiFetch(
      path,
      {
        ...params,
        method: 'PUT',
        body: JSON.stringify(data),
      },
      false,
    )
  },
}
